import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.tsx';
import { API_BASE } from '../utils/apiEndpoints';
import './css/ClassEditPage.css';

function JoinClassPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const codeFromUrl = searchParams.get('code') || '';
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [code, setCode] = useState(codeFromUrl);
  const [classInfo, setClassInfo] = useState<{ id: number; name: string; already_member: boolean } | null>(null);
  const [loading, setLoading] = useState(false);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setCode((prev) => codeFromUrl || prev);
  }, [codeFromUrl]);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
      return;
    }
  }, [authLoading, isAuthenticated, navigate]);

  const fetchClassByCode = async (codeToCheck?: string) => {
    const c = (codeToCheck ?? code).trim();
    if (!c) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/classes/join/${encodeURIComponent(c)}`, {
        credentials: 'include',
      });
      if (res.status === 404) {
        setClassInfo(null);
        setError('Класс не найден');
        return;
      }
      if (!res.ok) throw new Error('Ошибка загрузки');
      const data = await res.json();
      setClassInfo({ id: data.id, name: data.name, already_member: data.already_member });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка');
      setClassInfo(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && codeFromUrl) fetchClassByCode(codeFromUrl);
  }, [isAuthenticated, codeFromUrl]);


  const handleJoin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) return;
    setJoining(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/classes/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ invite_code: code.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Не удалось вступить');
      navigate(`/class/${data.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка');
    } finally {
      setJoining(false);
    }
  };

  if (authLoading) {
    return (
      <div className="class-edit-page">
        <div className="class-edit-loading">Загрузка…</div>
      </div>
    );
  }

  return (
    <div className="class-edit-page">
      <div className="class-edit-card class-edit-create" style={{ maxWidth: '420px' }}>
        <h1>Вступить в класс</h1>
        <p className="class-edit-create-desc">
          Введите код приглашения или перейдите по ссылке, которую отправил учитель.
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            fetchClassByCode(code);
          }}
          style={{ marginBottom: '16px' }}
        >
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Код приглашения"
            className="class-edit-input"
          />
          <button type="submit" disabled={loading || !code.trim()} className="class-edit-btn class-edit-btn-primary" style={{ marginTop: '8px' }}>
            {loading ? 'Проверка…' : 'Проверить'}
          </button>
        </form>
        {classInfo && (
          <form onSubmit={handleJoin}>
            <p style={{ color: '#e8e8e8', marginBottom: '12px' }}>
              Класс: <strong>{classInfo.name}</strong>
              {classInfo.already_member && (
                <span style={{ color: '#00B8A3', marginLeft: '8px' }}>Вы уже в этом классе</span>
              )}
            </p>
            {!classInfo.already_member && (
              <button type="submit" disabled={joining} className="class-edit-btn class-edit-btn-primary">
                {joining ? 'Вступление…' : 'Вступить в класс'}
              </button>
            )}
            {classInfo.already_member && (
              <button
                type="button"
                className="class-edit-btn class-edit-btn-primary"
                onClick={() => navigate(`/class/${classInfo.id}`)}
              >
                Перейти в класс
              </button>
            )}
          </form>
        )}
        {error && <div className="class-edit-error" style={{ marginTop: '16px' }}>{error}</div>}
      </div>
    </div>
  );
}

export default JoinClassPage;
